import React from 'react';

import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import {
    allowListMemberFormSchema,
    type AllowListMemberFormSchemaValues,
} from 'src/features/parkingManagement/api/AllowListAPI/useCreateAllowListMember';
import AllowListMemberForm from 'src/features/parkingManagement/components/AllowLists/forms/AllowListMemberForm';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { type DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { cn } from 'src/utils/shadcn';

import type { AllowListMember } from 'src/features/parkingManagement/types/allowList';

interface AllowListMemberFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedMember?: AllowListMember;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (data: AllowListMemberFormSchemaValues, onSuccess: () => void) => void;
}

export default function AllowListMemberFormModal<TData>({
    selectedMember,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: AllowListMemberFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const defaultValues = React.useMemo(() => selectedMember ?? {}, [selectedMember]);

    const form = useForm<AllowListMemberFormSchemaValues>({
        resolver: zodResolver(allowListMemberFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [isModalOpen, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: AllowListMemberFormSchemaValues) => mutate(values, () => setIsModalOpen(false)),
        [mutate, form],
    );
    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button variant="outline" className={cn('ml-auto', className)}>
                        <FontAwesomeIcon className="text-primary-700" icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <AllowListMemberForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedMember && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
